import {
  DesktopFiltersDropdown,
  DesktopFiltersToolbar,
  DesktopFilterTag,
} from "features/filters/components";

import { DropdownMenuList } from "common/components/ui/DropdownMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { typedObjectEntries } from "common/helpers/utils";

import { AUDIENCE_FILTERS } from "../../../../../constants";
import useAudienceFilters from "../../../../../hooks/useAudienceFilters";
import AudienceFilterOptions from "../AudienceFilterOptions";

import type {
  AudienceFacets,
  AudienceFilters as AudienceFiltersType,
} from "../../../../../types";

interface AudienceDesktopFiltersProps {
  audienceFacets: AudienceFacets;
  filters: AudienceFiltersType;
  setFilters: (filters: AudienceFiltersType) => void;
  isMiningTargets: boolean;
  isLoadingFacets: boolean;
}

function getSelectedFilterLabels(
  filterKey: keyof Omit<AudienceFiltersType, "keyword">,
  filters: AudienceFiltersType,
  audienceFacets: AudienceFacets,
): string[] {
  if (filterKey === "premium") {
    if (filters.premium === undefined) {
      return [];
    }

    return [filters.premium ? "Premium" : "Not premium"];
  }

  if (!audienceFacets?.[filterKey]) {
    return filters[filterKey];
  }

  // filter out any undefined or null ids
  const facets = audienceFacets[filterKey]?.filter(({ id }) => !!id) ?? [];

  // map the ids to the facet names
  return filters[filterKey].map((id) => {
    const facet = facets.find((item) => item.id.toString() === id);
    return facet?.name ?? id;
  });
}

export default function AudienceDesktopFilters({
  audienceFacets,
  filters,
  setFilters,
  isMiningTargets,
  isLoadingFacets,
}: AudienceDesktopFiltersProps) {
  const { hasFilters, resetAllFilters, setFilterValue } = useAudienceFilters(
    filters,
    setFilters,
  );

  const clearAllFilters = hasFilters && resetAllFilters;

  const filterMenuProps = {
    audienceFacets,
    isLoadingFacets,
    filters,
    setFilters,
  };

  const toolbar = (
    <DesktopFiltersToolbar clearAllFilters={clearAllFilters}>
      {typedObjectEntries(AUDIENCE_FILTERS).map(([key, { icon, label }]) => {
        const selectedFiltersLabels = getSelectedFilterLabels(
          key,
          filters,
          audienceFacets,
        );

        if (!selectedFiltersLabels.length) {
          return null;
        }

        return (
          <DesktopFilterTag
            key={key}
            icon={icon}
            label={label}
            selectedFilters={selectedFiltersLabels}
            onRemove={() =>
              setFilterValue(key, key === "premium" ? undefined : [])
            }
          >
            <DropdownMenuList>
              <AudienceFilterOptions {...filterMenuProps} filterKey={key} />
            </DropdownMenuList>
          </DesktopFilterTag>
        );
      })}

      <DesktopFiltersDropdown
        filters={AUDIENCE_FILTERS}
        renderFilter={(filterKey) => (
          <AudienceFilterOptions {...filterMenuProps} filterKey={filterKey} />
        )}
        hasFilters={hasFilters}
      />
    </DesktopFiltersToolbar>
  );

  if (isMiningTargets) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="pointer-events-none flex flex-1 opacity-50">
            {toolbar}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          You will be able to filter and export your Audience after all targets
          are loaded
        </TooltipContent>
      </Tooltip>
    );
  }

  return toolbar;
}
